/**
 * supabaseCaseReviews.ts — CRUD-Schicht für `case_reviews` (Vier-Augen-Prinzip).
 *
 * Workflow siehe Migration 0011_case_reviews.sql:
 *   1. requestReview(caseId)            → Status 'in_progress' → 'review'
 *   2. listPendingReviews()             → für Reviewer-Übersicht
 *   3. getOpenReviewForCase(caseId)     → laden für /case/:id/review
 *   4. decideReview(reviewId, decision) → Approve/Reject + Status-Update
 *
 * RLS regelt Sichtbarkeit/Berechtigung; wir validieren clientseitig nur die
 * UI-Pfade (z.B. UUID-Format).
 */

import { getSupabaseClient } from './supabase';
import type { CaseReviewRow, Database } from './supabaseTypes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Setzt den Case in den Review-Status und legt einen offenen Review-Eintrag an.
 * Wird vom Cockpit aus dem Owner-Berater aufgerufen.
 */
export async function requestReview(caseId: string, comment?: string): Promise<CaseReviewRow> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('requestReview: caseId ist kein UUID-Format');
  }
  const supa = getSupabaseClient();
  const { data: userData } = await supa.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error('requestReview: kein authentifizierter User');

  // Schritt 1: Case-Status auf 'review' setzen
  const { error: caseErr } = await supa
    .from('cases')
    .update({ status: 'review' } as never)
    .eq('id', caseId);
  if (caseErr) {
    console.error('requestReview: case status update failed', caseErr);
    throw new Error(`requestReview (case): ${caseErr.message}`);
  }

  // Schritt 2: Review-Eintrag anlegen
  const insert: Database['public']['Tables']['case_reviews']['Insert'] = {
    case_id: caseId,
    requested_by: uid,
    reviewer_id: null,
    decided_at: null,
    decision: null,
    comment: comment ?? null,
  };
  const { data, error } = await supa
    .from('case_reviews')
    .insert(insert as never)
    .select('*')
    .single();
  if (error) {
    console.error('requestReview: insert failed', error);
    throw new Error(`requestReview [${error.code ?? '?'}]: ${error.message}`);
  }
  return data;
}

/**
 * Liefert den OFFENEN Review-Eintrag eines Case (decided_at IS NULL).
 * Es kann pro Case maximal einer existieren — der Workflow erlaubt erst
 * einen neuen Request, wenn der vorherige entschieden ist (durch
 * Approve → Status 'completed' / Reject → Owner setzt manuell zurück).
 */
export async function getOpenReviewForCase(caseId: string): Promise<CaseReviewRow | null> {
  if (!UUID_RE.test(caseId)) return null;
  const supa = getSupabaseClient();
  const { data, error } = await supa
    .from('case_reviews')
    .select('*')
    .eq('case_id', caseId)
    .is('decided_at', null)
    .order('requested_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error('getOpenReviewForCase failed', error);
    throw new Error(`getOpenReviewForCase [${error.code ?? '?'}]: ${error.message}`);
  }
  return data;
}

/**
 * Liefert ALLE Reviews eines Case (offen + entschieden), sortiert nach
 * requested_at desc — für Audit-Trail in der Cockpit-Ansicht.
 */
export async function listReviewsForCase(caseId: string): Promise<CaseReviewRow[]> {
  if (!UUID_RE.test(caseId)) return [];
  const supa = getSupabaseClient();
  const { data, error } = await supa
    .from('case_reviews')
    .select('*')
    .eq('case_id', caseId)
    .order('requested_at', { ascending: false });
  if (error) {
    console.error('listReviewsForCase failed', error);
    throw new Error(`listReviewsForCase [${error.code ?? '?'}]: ${error.message}`);
  }
  return data;
}

/**
 * Listet alle offenen Reviews, die der eingeloggte User bearbeiten DARF
 * (Berater desselben Tenants, nicht der Owner). RLS filtert serverseitig.
 */
export async function listPendingReviews(): Promise<CaseReviewRow[]> {
  const supa = getSupabaseClient();
  const { data, error } = await supa
    .from('case_reviews')
    .select('*')
    .is('decided_at', null)
    .order('requested_at', { ascending: false });
  if (error) {
    console.error('listPendingReviews failed', error);
    throw new Error(`listPendingReviews [${error.code ?? '?'}]: ${error.message}`);
  }
  return data;
}

export interface DecideReviewInput {
  readonly decision: 'approve' | 'reject';
  readonly comment?: string;
}

/**
 * Trifft eine Review-Entscheidung. Bei Approve wandert der zugehörige Case
 * auf Status 'completed', bei Reject bleibt er 'review' (Owner kann
 * nachbessern und erneut requestReview() aufrufen).
 *
 * Trigger-Constraint (case_reviews_enforce_separation): reviewer_id darf
 * weder requested_by noch case.owner_id sein → DB lehnt das ab.
 */
export async function decideReview(
  reviewId: string,
  caseId: string,
  input: DecideReviewInput,
): Promise<CaseReviewRow> {
  if (!UUID_RE.test(reviewId) || !UUID_RE.test(caseId)) {
    throw new Error('decideReview: invalide UUID');
  }
  const supa = getSupabaseClient();
  const { data: userData } = await supa.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error('decideReview: kein authentifizierter User');

  const update: Database['public']['Tables']['case_reviews']['Update'] = {
    reviewer_id: uid,
    decided_at: new Date().toISOString(),
    decision: input.decision,
    comment: input.comment ?? null,
  };
  const { data, error } = await supa
    .from('case_reviews')
    .update(update as never)
    .eq('id', reviewId)
    .select('*')
    .single();
  if (error) {
    console.error('decideReview failed', error);
    throw new Error(`decideReview [${error.code ?? '?'}]: ${error.message}`);
  }

  if (input.decision === 'approve') {
    const { error: caseErr } = await supa
      .from('cases')
      .update({ status: 'completed', completed_at: new Date().toISOString() } as never)
      .eq('id', caseId);
    if (caseErr) {
      console.error('decideReview: case status update failed', caseErr);
      throw new Error(`decideReview (case): ${caseErr.message}`);
    }
  }

  return data;
}
